// Extracted from dashboard-layout.tsx (gap-fill for Plan
// 2026-05-06-meta-oauth-server-side-state, Task 10).
//
// Two listeners watch for OAuth-popup completion:
//   1. `message` — the popup posts {type: 'META_SIGNUP_CODE', code, wsPublicId,
//      state} via window.opener.postMessage when window.opener is reachable.
//   2. `storage` — fallback for the cases where window.opener is null
//      (tunnel interstitial, cross-origin redirect chain). The popup writes
//      `meta_signup_code` to localStorage and the parent tab observes the
//      storage event.
//
// Both listeners can fire for the same OAuth completion. `seenStatesRef`
// dedups by `state` (the server-issued nonce.suffix string is unique per
// mint), so the navigate runs exactly once.
//
// Lives in its own module so it can be unit-tested without rendering any
// layout. Consumed via useMetaOAuthCompletion below, mounted ONCE at the
// router root (App.tsx MetaOAuthCompletionMount) so the connect popup works
// from any page without per-layout wiring.
import { useEffect, useRef, type MutableRefObject } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router';
import { ValidationError } from '@hookmyapp/observability/errors';
import { connectingPath } from '@/lib/invite-connect';
import { captureException } from '@/lib/sentry';
import {
  initFacebookSdk,
  addEmbeddedSignupListener,
  type WaEmbeddedSignupPayload,
} from '@/lib/meta-signup';
import { writePopupContext, isPendingState } from '@/lib/meta-popup-context';

/**
 * App-wide OAuth-completion wiring: FB SDK init, the WA_EMBEDDED_SIGNUP
 * session-context listener, and the META_SIGNUP_CODE postMessage +
 * localStorage-relay listeners. Mounted once at the router root (App.tsx)
 * so a connect popup launched from ANY page is caught — per-layout wiring
 * was how the OAuth code got dropped on the org page (2026-07-15 bug).
 */
export function useMetaOAuthCompletion(): void {
  const navigate = useNavigate();
  const popupContextRef = useRef<WaEmbeddedSignupPayload | null>(null);
  useEffect(() => {
    initFacebookSdk();
    return addEmbeddedSignupListener((payload) => {
      popupContextRef.current = payload;
    });
  }, []);
  useMetaSignupListeners(navigate, popupContextRef);
}

interface MetaSignupCodePayload {
  code: string;
  wsPublicId: string;
  state: string;
  channelType?: string;
}

/**
 * Mounts `message` + `storage` listeners that navigate to
 * /w/:ws/channels/connecting?code=...&state=... when an OAuth popup or
 * localStorage relay delivers a META_SIGNUP_CODE payload. Dedups by `state`
 * so dual delivery (both listeners firing for the same completion) only
 * navigates once. Cleans both listeners up on unmount.
 */
export function useMetaSignupListeners(
  navigate: NavigateFunction,
  popupContextRef: MutableRefObject<WaEmbeddedSignupPayload | null>,
): void {
  // Tracks (state) values we've already navigated for. First wins; duplicate
  // from the other listener is dropped silently. useRef so the Set is stable
  // across React StrictMode dev double-mount of the listener effect — a
  // fresh `new Set()` per mount would re-allow duplicates the second time.
  const seenStatesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const claim = (state: string): boolean => {
      const seen = seenStatesRef.current;
      if (seen.has(state)) return false;
      seen.add(state);
      return true;
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'META_SIGNUP_CODE') return;
      const { code, wsPublicId, state, channelType } = event.data as MetaSignupCodePayload;
      if (!wsPublicId || !code || !state) {
        captureException(
          new ValidationError({
            message: 'META_SIGNUP_CODE missing required field',
            code: 'META_SIGNUP_CODE_INVALID_PAYLOAD',
          }),
        );
        return;
      }
      if (!claim(state)) return;
      writePopupContext(state, {
        source: 'opener_postmessage',
        payload: popupContextRef.current,
      });
      // One-shot: a later flow that never posts WA_EMBEDDED_SIGNUP must not
      // inherit this completion's WABA/phone selection.
      popupContextRef.current = null;
      navigate(
        connectingPath(
          wsPublicId,
          `code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&channelType=${encodeURIComponent(channelType ?? 'whatsapp')}`,
        ),
      );
    };

    // localStorage relay fallback (tunnel interstitial breaks window.opener).
    // Phase 118: payload shape changed from raw code string to JSON
    // `{code, wsPublicId, state}` — parse accordingly.
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'meta_signup_code' || !event.newValue) return;
      try {
        const { code, wsPublicId, state, channelType } = JSON.parse(
          event.newValue,
        ) as MetaSignupCodePayload;
        // storage events fire in EVERY same-origin tab. Only the tab that
        // launched this flow (recorded the minted state at oauth/start) may
        // consume the one-shot completion — and only it clears the relay key,
        // so the owning tab still sees the event.
        if (state && !isPendingState(state)) return;
        localStorage.removeItem('meta_signup_code');
        if (!wsPublicId || !code || !state) {
          throw new ValidationError({
            message: 'meta_signup_code missing required field',
            code: 'META_SIGNUP_CODE_INVALID_PAYLOAD',
          });
        }
        if (!claim(state)) return;
        writePopupContext(state, {
          source: 'storage_relay',
          payload: null,
        });
        navigate(
          connectingPath(
            wsPublicId,
            `code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&channelType=${encodeURIComponent(channelType ?? 'whatsapp')}`,
          ),
        );
      } catch (e) {
        captureException(e);
        localStorage.removeItem('meta_signup_code');
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [navigate]);
}
